'use client';

import { BodyDefault } from '../../../../components/typography';
import { Alert } from '../../../../components/alert';
import { Input } from '../../../../components/input';
import { KeyValueSection } from '../../../../components/key-value-section/KeyValueSection';
import { Select } from '../../../../components/select';
import { Switch } from '../../../../components/switch';
import { Tooltip } from '../../../../components/tooltip';
import { bodySignatureAlgorithmOptions } from '../../../application-credentials';
import { Box, HStack, token, VStack } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  HelpCircleIcon,
  InformationCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import type { BodySignatureAlgorithmType } from '@terra/shared-schemas-orchestrator';
import {
  createEmptyCredentialCookie,
  type KeyValuePair,
  useAddCredentialsDialogStore,
} from '../AddCredentialsDialogStore';
import { MtlsFields } from '../components/MtlsFields';
import { useShallow } from 'zustand/shallow';
import { Field } from '../../../../components/field';

export const RequestAuthenticationStep = () => {
  const t = useTranslations(
    'designSystem.credentials.addDialog.steps.requestAuthentication',
  );
  const {
    credential,
    formMeta,
    setCredential,
    setFormMeta,
    validationErrors,
    setValidationErrors,
  } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      credential: state.credential,
      formMeta: state.formMeta,
      setCredential: state.setCredential,
      setFormMeta: state.setFormMeta,
      validationErrors: state.validationErrors,
      setValidationErrors: state.setValidationErrors,
    })),
  );
  const requestSigning = formMeta.requestSigning;
  const updateRequestSigning = useCallback(
    (updates: Partial<typeof requestSigning>) => {
      const next = { ...requestSigning, ...updates };

      setFormMeta({ requestSigning: next });
      setCredential({
        bodySignature:
          next.headerName.trim() && next.signingSecret.trim()
            ? {
                headerName: next.headerName,
                secret: next.signingSecret,
                algorithm: next.algorithm,
              }
            : null,
      });
    },
    [requestSigning, setFormMeta, setCredential],
  );
  const updateHeaderPair = useCallback(
    (index: number, field: keyof KeyValuePair, value: string) => {
      const updated = [...credential.headers];

      updated[index] = { ...updated[index], [field]: value };
      setCredential({ headers: updated });
    },
    [credential.headers, setCredential],
  );
  const updateCookiePair = useCallback(
    (index: number, field: keyof KeyValuePair, value: string) => {
      const updated = [...credential.cookies];
      const cookieField = field === 'key' ? 'name' : 'value';

      updated[index] = { ...updated[index], [cookieField]: value };
      setCredential({ cookies: updated });
    },
    [credential.cookies, setCredential],
  );
  const updateSecretPair = useCallback(
    (index: number, field: keyof KeyValuePair, value: string) => {
      const updated = [...formMeta.secretKeys];

      updated[index] = { ...updated[index], [field]: value };
      setFormMeta({ secretKeys: updated });
      const filledSecrets = updated.filter(
        ({ key, value: v }) => key.trim() || v.trim(),
      );

      setCredential({ secrets: filledSecrets });
    },
    [formMeta.secretKeys, setFormMeta, setCredential],
  );
  const addHeaderPair = useCallback(() => {
    setCredential({
      headers: [...credential.headers, { key: '', value: '' }],
    });
  }, [credential.headers, setCredential]);
  const addCookiePair = useCallback(() => {
    setCredential({
      cookies: [...credential.cookies, createEmptyCredentialCookie()],
    });
  }, [credential.cookies, setCredential]);
  const addSecretPair = useCallback(() => {
    setFormMeta({
      secretKeys: [...formMeta.secretKeys, { key: '', value: '' }],
    });
  }, [formMeta.secretKeys, setFormMeta]);
  const removeHeaderPair = useCallback(
    (index: number) => {
      const updated = credential.headers.filter((_, i) => i !== index);

      setCredential({
        headers: updated.length > 0 ? updated : [{ key: '', value: '' }],
      });
    },
    [credential.headers, setCredential],
  );
  const removeCookiePair = useCallback(
    (index: number) => {
      const updated = credential.cookies.filter((_, i) => i !== index);

      setCredential({
        cookies: updated.length > 0 ? updated : [createEmptyCredentialCookie()],
      });
    },
    [credential.cookies, setCredential],
  );
  const removeSecretPair = useCallback(
    (index: number) => {
      const updated = formMeta.secretKeys.filter((_, i) => i !== index);
      const newSecretKeys =
        updated.length > 0 ? updated : [{ key: '', value: '' }];

      setFormMeta({ secretKeys: newSecretKeys });
      const filledSecrets = newSecretKeys.filter(
        ({ key, value }) => key.trim() || value.trim(),
      );

      setCredential({ secrets: filledSecrets });
    },
    [formMeta.secretKeys, setFormMeta, setCredential],
  );
  const cookiesAsKeyValue: KeyValuePair[] = credential.cookies.map((c) => ({
    key: c.name,
    value: c.value,
  }));

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <Alert.Root
        bg="bg.vulnerability.cold"
        borderColor="border.vulnerability.cold"
        px="2"
        py="1.5"
        gap={2}
      >
        <Alert.Icon asChild>
          <InformationCircleIcon
            color={token('colors.fg.vulnerability.cold')}
            size={20}
          />
        </Alert.Icon>
        <Alert.Content>
          <Alert.Description fontSize="sm" color="fg.neutral.default">
            {t('infoMessage')}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>

      <KeyValueSection
        label={t('headers.label')}
        keyPlaceholder={t('headers.keyPlaceholder')}
        valuePlaceholder={t('headers.valuePlaceholder')}
        addLabel={t('headers.add')}
        deleteAriaLabel={t('delete')}
        items={credential.headers}
        onItemChange={(index, field, value) =>
          updateHeaderPair(index, field, value)
        }
        onAdd={addHeaderPair}
        onRemove={removeHeaderPair}
      />

      <KeyValueSection
        label={t('cookies.label')}
        keyPlaceholder={t('cookies.keyPlaceholder')}
        valuePlaceholder={t('cookies.valuePlaceholder')}
        addLabel={t('cookies.add')}
        deleteAriaLabel={t('delete')}
        items={cookiesAsKeyValue}
        onItemChange={(index, field, value) =>
          updateCookiePair(index, field, value)
        }
        onAdd={addCookiePair}
        onRemove={removeCookiePair}
      />

      <KeyValueSection
        label={t('secretKeys.label')}
        keyPlaceholder={t('secretKeys.keyPlaceholder')}
        valuePlaceholder={t('secretKeys.valuePlaceholder')}
        addLabel={t('secretKeys.add')}
        deleteAriaLabel={t('delete')}
        tooltip={t('secretKeys.tooltip')}
        items={formMeta.secretKeys}
        onItemChange={(index, field, value) =>
          updateSecretPair(index, field, value)
        }
        onAdd={addSecretPair}
        onRemove={removeSecretPair}
      />

      <VStack alignItems="stretch" gap={1.5} w="full">
        <HStack gap={1} alignItems="center">
          <Field.Label fontSize="xs">{t('mtls.label')}</Field.Label>
          <Tooltip title={t('mtls.tooltip')} positioning={{ placement: 'top' }}>
            <HelpCircleIcon size={16} cursor="pointer" />
          </Tooltip>
        </HStack>
        <Switch
          size="sm"
          checked={formMeta.mtls.enabled}
          onCheckedChange={({ checked }) => {
            setFormMeta({
              mtls: {
                ...formMeta.mtls,
                enabled: checked,
                ...(!checked && {
                  certFileName: '',
                  keyFileName: '',
                  pfxFileName: '',
                }),
              },
            });
            if (!checked) {
              setCredential({
                mtlsOrigin: '',
                mtlsCert: '',
                mtlsKey: '',
                mtlsPfx: '',
                mtlsPassphrase: '',
              });
              setValidationErrors({
                mtlsOrigin: '',
                mtlsCertificate: '',
                mtlsKey: '',
              });
            }
          }}
        >
          <BodyDefault as="span" color="fg.neutral.muted">
            {formMeta.mtls.enabled ? t('mtls.enabled') : t('mtls.disabled')}
          </BodyDefault>
        </Switch>
      </VStack>

      {formMeta.mtls.enabled && (
        <MtlsFields
          mtlsCredential={{
            mtlsCert: credential.mtlsCert,
            mtlsKey: credential.mtlsKey,
            mtlsPfx: credential.mtlsPfx,
            mtlsOrigin: credential.mtlsOrigin,
            mtlsPassphrase: credential.mtlsPassphrase,
          }}
          mtlsMeta={formMeta.mtls}
          validationErrors={validationErrors}
          onCredentialChange={setCredential}
          onMetaChange={(meta) => setFormMeta({ mtls: meta })}
          onValidationErrorsClear={setValidationErrors}
          t={t}
        />
      )}

      <VStack alignItems="stretch" gap={1.5} w="full">
        <HStack gap={1} alignItems="center">
          <Field.Label fontSize="xs">{t('requestSigning.label')}</Field.Label>
          <Tooltip
            title={t('requestSigning.tooltip')}
            positioning={{ placement: 'top' }}
          >
            <HelpCircleIcon size={16} cursor="pointer" />
          </Tooltip>
        </HStack>
        <HStack gap={2} alignItems="flex-end" w="full">
          <Box flex={1}>
            <Input
              size="sm"
              placeholder={t('requestSigning.headerPlaceholder')}
              value={formMeta.requestSigning.headerName}
              onChange={(e) =>
                updateRequestSigning({ headerName: e.target.value })
              }
            />
          </Box>
          <Box flex={1}>
            <Input
              size="sm"
              placeholder={t('requestSigning.secretPlaceholder')}
              value={formMeta.requestSigning.signingSecret}
              onChange={(e) =>
                updateRequestSigning({ signingSecret: e.target.value })
              }
            />
          </Box>
          <Box w="120px" flexShrink={0}>
            <Select.Root
              size="sm"
              positioning={{ sameWidth: true, strategy: 'fixed' }}
              value={[formMeta.requestSigning.algorithm]}
              collection={bodySignatureAlgorithmOptions}
              onValueChange={({ value }) =>
                updateRequestSigning({
                  algorithm: (value[0] ??
                    'sha256') as BodySignatureAlgorithmType,
                })
              }
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText fontSize="xs" />
                  <ArrowDown01Icon size={16} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {bodySignatureAlgorithmOptions.items.map((option) => (
                      <Select.Item key={option.value} item={option}>
                        <Select.ItemText>{option.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
        </HStack>
      </VStack>
    </VStack>
  );
};
